/**
 * Keyboard Shortcuts Handler for RepuGate Dashboard
 * K = toggle shortcuts help
 * N = next review
 * R = regenerate with different tone
 * C = copy selected reply
 * ↑↓ = navigate replies
 * Enter = select reply
 */

import React from 'react';

export const KEYBOARD_SHORTCUTS = {
  NEXT_REVIEW: 'n',
  PREV_REVIEW: 'p',
  REGENERATE: 'r',
  COPY: 'c',
  ARROW_UP: 'ArrowUp',
  ARROW_DOWN: 'ArrowDown',
  ENTER: 'Enter',
  HELP: 'k'
};

/**
 * Hook to register keyboard shortcuts
 */
export function useKeyboardShortcuts(handlers) {
  React.useEffect(() => {
    const handleKeyPress = (e) => {
      // Ignore if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      const key = e.key.toLowerCase();

      switch (key) {
        case KEYBOARD_SHORTCUTS.NEXT_REVIEW:
          e.preventDefault();
          if (handlers.onNextReview) handlers.onNextReview();
          break;
        case KEYBOARD_SHORTCUTS.PREV_REVIEW:
          e.preventDefault();
          if (handlers.onPrevReview) handlers.onPrevReview();
          break;
        case KEYBOARD_SHORTCUTS.REGENERATE:
          e.preventDefault();
          if (handlers.onRegenerate) handlers.onRegenerate();
          break;
        case KEYBOARD_SHORTCUTS.COPY:
          e.preventDefault();
          if (handlers.onCopy) handlers.onCopy();
          break;
        case KEYBOARD_SHORTCUTS.HELP:
          e.preventDefault();
          if (handlers.onToggleHelp) handlers.onToggleHelp();
          break;
        case KEYBOARD_SHORTCUTS.ARROW_UP:
          if (handlers.onPrevOption) handlers.onPrevOption();
          break;
        case KEYBOARD_SHORTCUTS.ARROW_DOWN:
          if (handlers.onNextOption) handlers.onNextOption();
          break;
        case KEYBOARD_SHORTCUTS.ENTER:
          if (handlers.onSelectReply) handlers.onSelectReply();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handlers]);
}

/**
 * Display keyboard shortcuts help modal
 */
export function KeyboardShortcutsHelp({ isOpen, onClose }) {
  if (!isOpen) return null;

  const shortcuts = [
    { key: 'N', action: 'Next review' },
    { key: 'P', action: 'Previous review' },
    { key: 'R', action: 'Regenerate with different tone' },
    { key: 'C', action: 'Copy selected reply' },
    { key: '↑ ↓', action: 'Navigate between reply options' },
    { key: 'Enter', action: 'Select highlighted reply' },
    { key: 'K', action: 'Show this help' }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-purple-500/30 rounded-xl shadow-2xl max-w-sm w-full p-6">
        <h3 className="text-xl font-bold text-white mb-4">⌨️ Keyboard Shortcuts</h3>
        
        <div className="space-y-2 mb-6">
          {shortcuts.map((s, idx) => (
            <div key={idx} className="flex justify-between items-center">
              <code className="bg-slate-700/50 px-2 py-1 rounded text-pink-400 font-mono text-sm">
                {s.key}
              </code>
              <span className="text-slate-300">{s.action}</span>
            </div>
          ))}
        </div>

        <button
          onClick={onClose}
          className="w-full py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition"
        >
          Close (K)
        </button>
      </div>
    </div>
  );
}

const keyboardShortcutsModule = {
  KEYBOARD_SHORTCUTS,
  useKeyboardShortcuts,
  KeyboardShortcutsHelp
};

export default keyboardShortcutsModule;
